import React from 'react';
import { ApolloError } from '@apollo/client';
import { Card, CardContent, Box, Unstable_Grid2 as Grid } from '@mui/material';

import { ProductGroupFragment } from '@occc/graphql';
import {
  Typography,
  CircularProgress,
  LinearProgress,
  InfoField,
  Divider,
  ChipBox,
  ChipType,
} from '@occc-app/design-system';

import { productGroupKeysMap } from '../constants';

interface ProductGroupDetailsProps {
  loading: boolean;
  error?: ApolloError;
  productGroup?: ProductGroupFragment | null;
}

export const ProductGroupDetails: React.FC<ProductGroupDetailsProps> = ({
  loading,
  error,
  productGroup,
}) => {
  if (error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }
  if (loading && !productGroup) {
    return <CircularProgress />;
  }

  if (!productGroup) {
    return <Typography color='error'>No data. Something went wrong. Please try again.</Typography>;
  }

  const tarEligibleInfo = productGroup.fuelProductGroup?.userSelectable
    ? 'Eligible'
    : 'Not eligible';
  const tarDescription = `${tarEligibleInfo}${
    productGroup.fuelProductGroup?.default ? ' •  Default for all members' : ''
  }`;

  const badgeItems: ChipType[] = productGroup.values.map<ChipType>((value) => ({
    id: value,
    name: value,
  }));

  return (
    <Card>
      {loading && <LinearProgress />}
      <CardContent>
        <Grid container spacing={3}>
          <Grid xs={12}>
            <Typography variant='h6'>General Details</Typography>
          </Grid>

          <Grid container xs={12}>
            <Grid xs={12} md={6}>
              <InfoField label='Name' value={productGroup.name} mb={0} />
            </Grid>
            <Grid xs={12} md={6}>
              <InfoField label='Description' value={productGroup.description} mb={0} />
            </Grid>
          </Grid>

          <Grid xs={12}>
            <Typography variant='subtitle1'>
              Configured {productGroupKeysMap[productGroup.key].toLowerCase()}s
            </Typography>
          </Grid>

          <Grid container xs={12} px={3}>
            <Grid xs={12} display='flex' flexDirection='row' alignItems='center'>
              <Typography>Total</Typography>
              <Box bgcolor='primary.light' color='primary.main' px={0.5} ml={1} borderRadius={1}>
                {productGroup.values.length}
              </Box>
            </Grid>

            <ChipBox items={badgeItems} />
          </Grid>
        </Grid>
        <Divider />
        <Grid container mt={3} xs={12} spacing={3}>
          <Grid xs={12} sm={6}>
            <InfoField label='Total Available Rollback' value={tarDescription} mb={0} />
          </Grid>
          {!!productGroup.fuelProductGroup && productGroup.fuelProductGroup.userSelectable && (
            <Grid xs={12} sm={6}>
              <InfoField
                label='Product Group Rank'
                value={productGroup.fuelProductGroup?.sortingRank}
                mb={0}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
